import { useNavigate } from 'react-router-dom';
import { useSiteContent } from '../data/queries';
import { MTCLogo } from './Logo';

const FALLBACK_TAGLINE =
  "Centre marocain de formation continue dédié aux professionnels de l'art dentaire.";
const FALLBACK_ADDRESS = 'Boulevard Zerktouni\nCasablanca, Maroc';
const FALLBACK_PHONE = '+212 7 07 70 76 06';
const FALLBACK_EMAIL = 'contact@mtc.ma';
const FALLBACK_LEGAL = 'Mentions légales · Confidentialité · CGV';

/** Backup defaults — only used if admin hasn't filled siteContent.socials yet. */
const FALLBACK_INSTAGRAM = 'https://www.instagram.com/moroccantraining.center/';

/** Normalise admin-pasted values: full URL, handle, or "@handle" all work. */
function normaliseSocialUrl(
  raw: string | undefined,
  platform: 'instagram' | 'facebook' | 'tiktok',
): string | null {
  const v = raw?.trim();
  if (!v) return null;
  if (/^https?:\/\//i.test(v)) return v;
  const handle = v.replace(/^@+/, '');
  if (platform === 'instagram') return `https://www.instagram.com/${handle}/`;
  if (platform === 'facebook') return `https://www.facebook.com/${handle}`;
  return `https://www.tiktok.com/@${handle}`;
}

export function Footer() {
  const navigate = useNavigate();
  const siteQ = useSiteContent();
  const site = siteQ.data;

  const tagline = site?.footer?.tagline?.trim() || FALLBACK_TAGLINE;
  const address = site?.contact?.address?.trim() || FALLBACK_ADDRESS;
  const phone = site?.contact?.phone?.trim() || FALLBACK_PHONE;
  const email = site?.contact?.email?.trim() || FALLBACK_EMAIL;
  const legal = site?.footer?.legal?.trim() || FALLBACK_LEGAL;
  const year = new Date().getFullYear();

  // Resolve each social: admin value first, Instagram fallback for the launch
  // period where the admin hasn't filled siteContent.socials yet.
  const instagramHref =
    normaliseSocialUrl(site?.socials?.instagram, 'instagram') ?? FALLBACK_INSTAGRAM;
  const facebookHref = normaliseSocialUrl(site?.socials?.facebook, 'facebook');
  const tiktokHref = normaliseSocialUrl(site?.socials?.tiktok, 'tiktok');

  return (
    <footer className="mtc-footer">
      <div className="mtc-footer-inner">
        <div className="mtc-footer-col">
          <MTCLogo mono size={72} />
          <p className="mtc-footer-tag">{tagline}</p>
        </div>
        <div className="mtc-footer-col">
          <h4>Formations</h4>
          <button type="button" onClick={() => navigate('/formations')}>
            Toutes les formations
          </button>
          <button type="button" onClick={() => navigate('/calendrier')}>
            Calendrier
          </button>
          <button type="button" onClick={() => navigate('/inscription')}>
            S'inscrire
          </button>
        </div>
        <div className="mtc-footer-col">
          <h4>Centre</h4>
          <button type="button" onClick={() => navigate('/a-propos')}>
            À propos
          </button>
          <button type="button" onClick={() => navigate('/formateurs')}>
            Formateurs
          </button>
          <button type="button" onClick={() => navigate('/confidentialite')}>
            Confidentialité
          </button>
        </div>
        <div className="mtc-footer-col">
          <h4>Contact</h4>
          <p style={{ whiteSpace: 'pre-line' }}>{address}</p>
          <p>
            <a href={`tel:${phone.replace(/\s+/g, '')}`} style={{ color: 'inherit', textDecoration: 'none' }}>
              {phone}
            </a>
            <br />
            <a href={`mailto:${email}`} style={{ color: 'inherit', textDecoration: 'none' }}>
              {email}
            </a>
          </p>
          <div
            className="mtc-footer-socials"
            aria-label="Réseaux sociaux MTC"
          >
            <SocialPill href={instagramHref} platform="instagram" />
            {facebookHref && <SocialPill href={facebookHref} platform="facebook" />}
            {tiktokHref && <SocialPill href={tiktokHref} platform="tiktok" />}
          </div>
        </div>
      </div>
      <div className="mtc-footer-bottom">
        <span>© {year} Moroccan Training Center</span>
        <span>{legal}</span>
      </div>
    </footer>
  );
}

// ───────────── Social pill ─────────────

type Platform = 'instagram' | 'facebook' | 'tiktok';

const LABELS: Record<Platform, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
};

function SocialPill({ href, platform }: { href: string; platform: Platform }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`mtc-footer-social mtc-footer-social-${platform}`}
      aria-label={`Suivre MTC sur ${LABELS[platform]}`}
      title={LABELS[platform]}
    >
      <SocialIcon platform={platform} />
    </a>
  );
}

function SocialIcon({ platform }: { platform: Platform }) {
  if (platform === 'instagram') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          fill="currentColor"
          d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.72 3.72 0 0 1-1.38-.9 3.72 3.72 0 0 1-.9-1.38c-.16-.42-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16M12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.88 5.88 0 0 0-2.13 1.38A5.88 5.88 0 0 0 .63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91a5.88 5.88 0 0 0 1.38 2.13 5.88 5.88 0 0 0 2.13 1.38c.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.88 5.88 0 0 0 2.13-1.38 5.88 5.88 0 0 0 1.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.88 5.88 0 0 0-1.38-2.13A5.88 5.88 0 0 0 19.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84a6.16 6.16 0 1 0 0 12.32 6.16 6.16 0 0 0 0-12.32zm0 10.16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-11.85a1.44 1.44 0 1 0 0 2.88 1.44 1.44 0 0 0 0-2.88z"
        />
      </svg>
    );
  }
  if (platform === 'facebook') {
    return (
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path
          fill="currentColor"
          d="M22.675 0H1.325C.593 0 0 .593 0 1.325v21.351C0 23.408.593 24 1.325 24H12.82v-9.294H9.692V11.08h3.128V8.412c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.464.099 2.796.143v3.24h-1.918c-1.504 0-1.795.715-1.795 1.763v2.31h3.59l-.467 3.626h-3.123V24h6.116c.73 0 1.323-.592 1.323-1.324V1.325C24 .593 23.408 0 22.675 0z"
        />
      </svg>
    );
  }
  // TikTok
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        fill="currentColor"
        d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-5.86 1.7 6.13 6.13 0 0 0-2.07 4.6 6.18 6.18 0 0 0 11.61 2.92 6.34 6.34 0 0 0 .65-2.86V8.66a8.18 8.18 0 0 0 4.9 1.59V6.78a4.85 4.85 0 0 1-.01-.09z"
      />
    </svg>
  );
}
